import {
  ChevronDownOutlineIcon,
  ChevronUpOutlineIcon,
  CloseOutlineIcon,
  DownloadOutlineIcon,
} from '@applyboard/ui-icons';
import React from 'react';
import { Box, Button, Flex, HelpText } from '~/components';
import { IntentProps, useFullTheme } from '~/design';
import { Label, SrOnly } from '~/internal';
import { useId } from '~/utils/styling';
import {
  FileIntentIcon,
  FileListContext,
  FileListContextProps,
  FileListIconButton,
  FileNameButtonWrapper,
  UploadingIndicator,
  getFileTypeFromMime,
  getFileTypeFromName,
} from './FileList.utils';
import { FormControlContainerProps } from '~/internal/FormControlContainer';

export interface FileListProps {
  /** The aria-label of the list. */
  'aria-label'?: string;

  /** The aria-labelledby of the list. */
  'aria-labelledby'?: string;

  /** The file list items. */
  children: React.ReactElement<FileListItemProps> | Array<React.ReactElement<FileListItemProps>>;

  /** If true, none of the items can be removed. (default: false) */
  disabled?: boolean;

  /** The emphasis of each FileList.Item against its background. (default: highlighted) */
  emphasis?: 'highlighted' | 'outlined';

  /** The label of the list. */
  label?: string;

  /** Determines how the label is placed relative to the FileList. (default: 'start') */
  labelPosition?: FormControlContainerProps['labelPosition'];

  /** The maximum number of items to show on the initial render. If 0, all items will be shown.
   * When > 0, if the number of items exceeds this value, a "Show more" button will appear and only
   * the first `maxItems` will be shown. (default: 0) */
  maxItems?: number;

  /** A callback function that is called when a file is removed. */
  onRemove?: FileListContextProps['onRemove'];

  /** The orientation of the items. If `horizontal`, the file items will only take up the amount of
   * width needed to show the filename, up to a max width of 220px, and will render the items as
   * though they were "inline" elements. Tooltips are attached to each item to allow the user to
   * see the full filename. The items will be displayed beside each other. If `vertical`, each item
   * will have a width of 100%, thus rendering items as "block" elements, and no tooltips will be
   * used. (default: horizontal) */
  orientation?: FileListContextProps['orientation'];

  /** The height of the items. (default: md) */
  size?: FileListContextProps['size'];
}

function FileList({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  children,
  disabled,
  emphasis = 'highlighted',
  label,
  labelPosition: _labelPosition,
  maxItems = 0,
  onRemove,
  orientation = 'horizontal',
  size = 'md',
}: FileListProps) {
  const listRef = React.useRef<HTMLUListElement | null>(null);
  const numItems = React.Children.count(children);
  const canShowMore = maxItems > 0 && numItems > 4;
  const [showMore, setShowMore] = React.useState(false);
  const theme = useFullTheme();

  const childrenToRender = React.Children.toArray(children).slice(
    0,
    !canShowMore || showMore ? numItems : maxItems,
  ) as unknown as React.ReactElement<FileListItemProps>[];
  const labelId = useId();

  if (numItems === 0) {
    return null;
  }

  if ([label, ariaLabel, ariaLabelledby].filter(Boolean).length > 1) {
    throw new Error('Only one of `label`, `aria-label`, `aria-labelledby` can be used.');
  }

  const labelPosition = _labelPosition
    ? _labelPosition
    : orientation === 'horizontal'
      ? 'start'
      : 'top';
  const isBlockDisplay = labelPosition === 'top';

  return (
    <FileListContext.Provider
      value={{
        disabled,
        handleItemRemoval: (index: number) => handleItemRemoval(listRef, index),
        numItems,
        onRemove,
        orientation,
        size,
      }}
    >
      <div>
        <Flex
          align="start"
          direction={isBlockDisplay ? 'column' : 'row'}
          gap={isBlockDisplay ? 2 : 4}
        >
          {label && (
            <Flex.Item shrink={0}>
              <Box pt={isBlockDisplay ? 0 : size === 'sm' ? 2 : 3}>
                <Label id={labelId} label={label} />
              </Box>
            </Flex.Item>
          )}

          <Flex.Item grow={1} minWidth={0} width="fill">
            <ul
              aria-label={ariaLabel}
              aria-labelledby={ariaLabelledby ? ariaLabelledby : label ? labelId : undefined}
              css={[
                {
                  display: 'flex',
                  gap: theme.spacing[3],
                  margin: 0,
                  padding: 0,
                  width: '100%',
                },
                orientation === 'horizontal' && {
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                },
                orientation === 'vertical' && {
                  flexDirection: 'column',
                },
              ]}
              ref={listRef}
            >
              {childrenToRender.map((child, index) => (
                <InnerFileListItem
                  {...child.props}
                  emphasis={child.props.emphasis ?? emphasis}
                  index={index}
                  key={child.props.value}
                />
              ))}

              {canShowMore && (
                <div>
                  <Button
                    emphasis="transparent"
                    intent="primary"
                    onClick={() => setShowMore(!showMore)}
                    size="sm"
                    trailIcon={
                      orientation === 'horizontal'
                        ? undefined
                        : showMore
                          ? ChevronUpOutlineIcon
                          : ChevronDownOutlineIcon
                    }
                  >
                    {showMore ? 'Show less' : 'Show more'}
                  </Button>
                </div>
              )}
            </ul>
          </Flex.Item>
        </Flex>

        {numItems > 0 && (
          <SrOnly>
            <div aria-live="polite">
              There {numItems === 1 ? 'is' : 'are'} {numItems} uploaded{' '}
              {numItems === 1 ? 'file' : 'files'}.
            </div>
          </SrOnly>
        )}
      </div>
    </FileListContext.Provider>
  );
}

export interface InnerFileListItemProps extends IntentProps {
  /** If true, none of the items can be removed. (default: false) */
  disabled?: boolean;

  /** The file type (MIME) of the file. Used for rendering item's filetype icon. If not supplied,
   * the extension in the `name` prop will be used. */
  fileType?: string;

  /** The emphasis of the item against its background. (default: highlighted) */
  emphasis?: FileListProps['emphasis'];

  /** Help text associated with the file. Useful if the file has any issues the user should know
   * about. */
  helpText?: string | React.ReactNode;

  /** The download link for the file. If supplied */
  href?: string;

  /** If true, hides the item's filetype icon. */
  hideIcon?: boolean;

  index: number;

  /** The file's name. */
  name: string;

  /** A callback function that is invoked when the file is opened. */
  onDownload?: FileListContextProps['onDownload'];

  /** A callback function that is invoked when the "X" button is pressed. If set to null,, no "X"
   * button will be rendered. */
  onRemove?: FileListContextProps['onRemove'];

  /** If true, the a spinner will appear where the item's download and remove actions would
   * normally render. */
  uploading?: boolean;

  /** A unique identifer for the file. */
  value: string;
}

function InnerFileListItem({
  disabled = false,
  fileType: _fileType,
  emphasis = 'highlighted',
  helpText,
  hideIcon = false,
  href,
  index,
  intent: _intent,
  name,
  onDownload,
  onRemove: _onRemove,
  uploading,
  value,
}: InnerFileListItemProps) {
  const context = React.useContext(FileListContext);
  if (!context) {
    throw new Error('Missing context');
  }
  const { spacing } = useFullTheme();
  const intent = _intent ?? 'secondary';
  const fileType = _fileType ? getFileTypeFromMime(_fileType) : getFileTypeFromName(name);
  const height = context.size === 'md' ? 48 : 40;

  const showRemoveButton = _onRemove !== null;
  const onRemove = React.useCallback(
    (id: string) => {
      if (!showRemoveButton) {
        return;
      }
      context.handleItemRemoval(index);
      if (_onRemove) {
        _onRemove(id);
      } else {
        context.onRemove?.(id);
      }
    },
    [context, index, _onRemove, showRemoveButton],
  );

  return (
    <li
      css={[
        {
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[2],
        },
        context.orientation === 'vertical' && {
          width: '100%',
        },
      ]}
    >
      <div
        css={{
          alignItems: 'center',
          display: 'flex',
          gap: spacing[2],
          height,
          width: '100%',
        }}
      >
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexGrow: 1,
            minWidth: 0,
          }}
        >
          <FileNameButtonWrapper
            fileType={fileType}
            emphasis={emphasis}
            hideIcon={hideIcon}
            intent={intent}
            name={name}
            href={href}
            removeFile={showRemoveButton ? onRemove : null}
            uploading={uploading}
          />

          <UploadingIndicator emphasis={emphasis} intent={intent} uploading={uploading} />

          {!uploading && (
            <Flex>
              {['warning', 'negative'].includes(intent) && (
                <FileIntentIcon intent={intent} isLast={!showRemoveButton && !href} />
              )}

              {!!href && (
                <FileListIconButton
                  aria-label={`Download file ${name}`}
                  emphasis={emphasis}
                  href={href}
                  icon={DownloadOutlineIcon}
                  intent={intent}
                  isLast={!showRemoveButton}
                  onClick={() => onDownload?.(value)}
                />
              )}

              {showRemoveButton && (
                <FileListIconButton
                  aria-label={`Remove file ${name}`}
                  disabled={disabled}
                  emphasis={emphasis}
                  icon={CloseOutlineIcon}
                  intent={intent}
                  isLast
                  onClick={() => onRemove(value)}
                />
              )}
            </Flex>
          )}
        </div>
      </div>

      {!!helpText && (
        <div css={{ marginBottom: index === context.numItems - 1 ? 0 : spacing[2] }}>
          {typeof helpText === 'string' ? (
            <HelpText intent={intent}>{helpText}</HelpText>
          ) : (
            helpText
          )}
        </div>
      )}
    </li>
  );
}

export interface FileListItemProps
  extends Pick<
      InnerFileListItemProps,
      | 'emphasis'
      | 'fileType'
      | 'helpText'
      | 'href'
      | 'hideIcon'
      | 'name'
      | 'onDownload'
      | 'onRemove'
      | 'uploading'
      | 'value'
    >,
    IntentProps {}

function FileListItem(props: FileListItemProps) {
  return <InnerFileListItem {...props} index={0} />;
}

// Because `FileList` uses `React.forwardRef`, we can't simply do `FileList.Item = FileListItem`
// as it results in a typescript error
const FileListExport = Object.assign(FileList, { Item: FileListItem, displayName: 'FileList' });

function handleItemRemoval(listRef: React.RefObject<HTMLUListElement>, index: number) {
  const querySelectorStr = 'button[data-btn-type="remove"]';
  const listItems = Array.from(listRef?.current?.querySelectorAll('li') ?? []);
  let btnToFocus: HTMLButtonElement | null = listItems[1]?.querySelector(querySelectorStr);
  if (index > 0) {
    // Ideally, when an item is removed, the next immediate item in the list is focused on so
    // that the focus ring stays "in place" in the UI
    const itemsOrdered = [...listItems.slice(index + 1), ...listItems.slice(0, index)];
    for (const item of itemsOrdered) {
      const btn = item.querySelector(querySelectorStr) as HTMLButtonElement;
      if (btn && !btn.disabled) {
        btnToFocus = btn as HTMLButtonElement;
        break;
      }
    }
  }
  if (btnToFocus) {
    btnToFocus.focus();
  } else {
    // Find anything else to focus on. If this fails, the consumer is responsible for rescuing
    (
      listRef?.current?.querySelectorAll('button, a')?.[0] as HTMLButtonElement | HTMLAnchorElement
    )?.focus();
  }
}

export { FileListExport as FileList };
